// A13 — API kalitlari va dasturchilar, QO'SHIMCHA ekranlar data-qatlami. Committed
// `devApi.ts` (module home) ga TEGMASLIK uchun alohida fayl (Ads/adsExtraApi naqshi).
// Real-wired: A13.02 ilova-kartasi + A13.03 statistika (apps ro'yxatidan hosila),
// A13.05 bloklash + A13.09 revoke (mavjud mutation'lar). Qolgani greenfield fail-soft:
// backend `/admin/developers/*` chiqmaguncha bo'sh, chiqqach aynen mos keladi.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'
import { type AppRow } from './devApi'

/* Real-wired mutation/fetcherlar committed devApi.ts da — u yerdan qayta eksport
 * qilamiz, shunda A13.05/09 ekranlari bitta import'dan foydalanadi. */
export {
  fetchApps,
  setAppStatus,
  fetchBotTokens,
  revokeBotToken,
  type AppRow,
  type BotTokenRow,
} from './devApi'

/* ── Local helperlar (devApi ichidagilar eksport qilinmagan — takrorlaymiz) ── */
function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/** Greenfield read-only jadval fetcher yasovchisi (fail-soft → bo'sh). */
function listFetcher<T>(path: string, key: string, search: (r: T) => string) {
  return (params: FetchParams): Promise<FetchResult<T>> =>
    safeList<T>(path, key, params.signal).then((rows) => toResult(rows, params, search))
}

/* ── A13.02 — Ilova kartasi (apps ro'yxatidan, REAL) ───────────────────── */
export function fetchApp(id: number, signal: AbortSignal): Promise<AppRow | null> {
  return safeList<AppRow>('/admin/developers/apps', 'apps', signal).then(
    (rows) => rows.find((r) => r.id === id) ?? null,
  )
}

/* ── A13.03 — Foydalanish statistikasi (apps ro'yxatidan hosila, REAL) ──── */
export interface UsageStats {
  total: number
  active: number
  blocked: number
  requests_today: number
  top: { name: string; owner: string; requests_today: number; status: string }[]
}
export function fetchUsageStats(signal: AbortSignal): Promise<UsageStats> {
  return safeList<AppRow>('/admin/developers/apps', 'apps', signal).then((rows) => {
    const active = rows.filter((r) => r.status === 'active').length
    const requests = rows.reduce((s, r) => s + (r.requests_today || 0), 0)
    const top = [...rows]
      .sort((a, b) => (b.requests_today || 0) - (a.requests_today || 0))
      .slice(0, 10)
      .map((r) => ({
        name: r.name,
        owner: r.owner,
        requests_today: r.requests_today || 0,
        status: r.status,
      }))
    return { total: rows.length, active, blocked: rows.length - active, requests_today: requests, top }
  })
}

/* ── A13.06 — Suiiste'mol aniqlash ─────────────────────────────────────── */
export interface AbuseRow {
  id: number
  app: string
  pattern: string
  severity: 'low' | 'medium' | 'high'
  requests: number
  detected_at: string
  status: string
}
export const fetchAbuse = listFetcher<AbuseRow>(
  '/admin/developers/abuse',
  'events',
  (r) => `${r.app} ${r.pattern}`,
)

/* ── A13.10 — VIP limitlar ─────────────────────────────────────────────── */
export interface VipLimitRow {
  id: number
  app: string
  owner: string
  per_minute: number
  per_day: number
  note: string
}
export const fetchVipLimits = listFetcher<VipLimitRow>(
  '/admin/developers/vip-limits',
  'limits',
  (r) => `${r.app} ${r.owner}`,
)

/* ── A13.11 — Kalit rotatsiyasi jurnali ────────────────────────────────── */
export interface RotationLogRow {
  id: number
  key_ref: string
  kind: string
  rotated_by: string
  rotated_at: string
  reason: string
}
export const fetchRotationLog = listFetcher<RotationLogRow>(
  '/admin/developers/rotation-log',
  'entries',
  (r) => `${r.key_ref} ${r.rotated_by}`,
)

/* ── A13.12 — Ichki servis kalitlari ───────────────────────────────────── */
export interface ServiceKeyRow {
  id: number
  service: string
  key_masked: string
  scope: string
  last_used: string | null
  status: 'active' | 'revoked'
}
export const fetchServiceKeys = listFetcher<ServiceKeyRow>(
  '/admin/developers/service-keys',
  'keys',
  (r) => `${r.service} ${r.scope}`,
)

/* ── A13.13 — Shifrlash kalitlari (KMS) — skeleton infra ───────────────── */
export interface KmsKeyRow {
  id: number
  alias: string
  purpose: string
  algorithm: string
  state: 'enabled' | 'disabled' | 'pending_deletion'
  created_at: string
}
export const fetchKmsKeys = listFetcher<KmsKeyRow>(
  '/admin/developers/kms-keys',
  'keys',
  (r) => `${r.alias} ${r.purpose}`,
)

/* ── A13.14 — Kalit eksporti (four-eyes + HSM) — skeleton infra ─────────── */
export interface KeyExportRow {
  id: number
  key_ref: string
  requested_by: string
  approver: string | null
  status: 'pending' | 'approved' | 'rejected' | 'exported'
  requested_at: string
}
export const fetchKeyExports = listFetcher<KeyExportRow>(
  '/admin/developers/key-exports',
  'requests',
  (r) => `${r.key_ref} ${r.requested_by}`,
)

/* ── A13.15 — Sertifikatlar (TLS) monitoringi — skeleton infra ─────────── */
export interface CertRow {
  id: number
  domain: string
  issuer: string
  expires_at: string
  days_left: number
  status: 'valid' | 'expiring' | 'expired'
}
export const fetchCerts = listFetcher<CertRow>(
  '/admin/developers/certs',
  'certs',
  (r) => `${r.domain} ${r.issuer}`,
)

/* ── A13.17 — Provayder kalitlari (to'lov) ─────────────────────────────── */
export interface ProviderKeyRow {
  id: number
  provider: string
  environment: 'test' | 'production'
  key_masked: string
  status: 'active' | 'revoked'
  updated_at: string
}
export const fetchProviderKeys = listFetcher<ProviderKeyRow>(
  '/admin/developers/provider-keys',
  'keys',
  (r) => `${r.provider} ${r.environment}`,
)

/* ── A13.19 — Kalit siyosati (muddat, murakkablik) ─────────────────────── */
export interface KeyPolicyRow {
  id: number
  name: string
  scope: string
  max_age_days: number
  min_length: number
  rotation_days: number
  enforced: boolean
}
export const fetchKeyPolicy = listFetcher<KeyPolicyRow>(
  '/admin/developers/key-policy',
  'rules',
  (r) => `${r.name} ${r.scope}`,
)

/* ── A13.20 — Kalit auditi ─────────────────────────────────────────────── */
export interface KeyAuditRow {
  id: number
  actor: string
  action: string
  key_ref: string
  ip: string
  at: string
}
export const fetchKeyAudit = listFetcher<KeyAuditRow>(
  '/admin/developers/key-audit',
  'entries',
  (r) => `${r.actor} ${r.action} ${r.key_ref}`,
)

/** A13 QO'SHIMCHA — modul-local RU strings (i18n HOT → keyга ko'chiriladi). */
export const DEVX_STR = {
  // A13.02 ilova kartasi
  appCard: 'Карточка приложения',
  appNotFound: 'Приложение не найдено',
  back: 'Назад',
  owner: 'Владелец',
  status: 'Статус',
  requestsToday: 'Запросов сегодня',
  created: 'Создан',
  block: 'Заблокировать',
  unblock: 'Разблокировать',
  active: 'Активно',
  blocked: 'Заблокировано',
  statusChanged: 'Статус обновлён',
  // A13.03 statistika
  usage: 'Статистика использования',
  totalApps: 'Всего приложений',
  activeApps: 'Активных',
  blockedApps: 'Заблокировано',
  totalRequests: 'Запросов сегодня',
  topApps: 'Топ приложений по запросам',
  name: 'Название',
  // A13.05 bloklash
  appBlock: 'Блокировка приложений',
  appBlockDesc: 'Блокировка и разблокировка API-приложений',
  // A13.06 suiiste'mol
  abuse: 'Обнаружение злоупотреблений',
  pattern: 'Паттерн',
  severity: 'Уровень',
  requests: 'Запросов',
  detectedAt: 'Обнаружено',
  // A13.09 revoke
  forcedRevoke: 'Принудительный отзыв',
  forcedRevokeDesc: 'Немедленный отзыв токенов ботов',
  bot: 'Бот',
  lastUsed: 'Последнее использование',
  revoke: 'Отозвать',
  revoked: 'Токен отозван',
  // A13.10 VIP limitlar
  vipLimits: 'VIP-лимиты',
  perMinute: 'Запросов/мин',
  perDay: 'Запросов/день',
  note: 'Примечание',
  // A13.11 rotatsiya jurnali
  rotationLog: 'Журнал ротации ключей',
  keyRef: 'Ключ',
  kind: 'Тип',
  rotatedBy: 'Кем сменён',
  rotatedAt: 'Когда',
  reason: 'Причина',
  // A13.12 servis kalitlari
  serviceKeys: 'Ключи внутренних сервисов',
  service: 'Сервис',
  key: 'Ключ',
  scope: 'Область',
  // A13.13 KMS
  kms: 'Ключи шифрования (KMS)',
  kmsDesc: 'Реестр KMS — данные ждут интеграции с провайдером',
  alias: 'Псевдоним',
  purpose: 'Назначение',
  algorithm: 'Алгоритм',
  state: 'Состояние',
  // A13.14 kalit eksporti
  keyExport: 'Экспорт ключей (four-eyes + HSM)',
  keyExportDesc: 'Экспорт ключей с двойным подтверждением через HSM',
  requestedBy: 'Запросил',
  approver: 'Подтвердил',
  requestedAt: 'Запрошен',
  // A13.15 sertifikatlar
  certs: 'Мониторинг сертификатов (TLS)',
  certsDesc: 'Срок действия TLS-сертификатов — ждёт интеграции с ACME/CA',
  domain: 'Домен',
  issuer: 'Издатель',
  expiresAt: 'Истекает',
  daysLeft: 'Дней осталось',
  // A13.17 provayder kalitlari
  providerKeys: 'Ключи провайдеров (платежи)',
  provider: 'Провайдер',
  environment: 'Среда',
  updated: 'Обновлён',
  // A13.19 kalit siyosati
  keyPolicy: 'Политика ключей',
  maxAge: 'Макс. возраст (дн.)',
  minLength: 'Мин. длина',
  rotationDays: 'Ротация (дн.)',
  enforced: 'Применяется',
  // A13.20 kalit auditi
  keyAudit: 'Аудит ключей',
  actor: 'Актор',
  action: 'Действие',
  ip: 'IP',
  at: 'Когда',
  // A13.13/14/15 — JONLI infra reyestrlari (CRUD) qo'shimcha yorliqlar
  rotated: 'Последняя ротация',
  addKms: 'Добавить ключ',
  addExport: 'Добавить запрос',
  addCert: 'Добавить сертификат',
  createdCol: 'Создан',
  // umumiy
  failed: 'Ошибка',
  empty: 'Нет данных',
} as const
